import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import FilmCard from '@/components/base/FilmCard';
import {
  films,
  genreOptionsByType,
  languageOptions,
  regionOptions,
  type FilmType,
  yearOptions,
} from '@/mocks/films';
import { applySeo } from '@/utils/seo';

type SortKey = 'updated' | 'popular' | 'rating';

interface Filters {
  region: string;
  year: string;
  language: string;
  genre: string;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '最近更新' },
  { key: 'popular', label: '热门程度' },
  { key: 'rating', label: '评分最高' },
];

const emptyFilters: Filters = {
  region: '全部地区',
  year: '全部年份',
  language: '全部语言',
  genre: '全部类型',
};

const PAGE_SIZE = 8;

const typeCopy: Record<FilmType, { title: string; desc: string; keyword: string }> = {
  movie: {
    title: '电影',
    desc: '院线新片、类型佳作与影史经典，按地区、年份、语言与题材自由筛选，找到今晚最适合的一部。',
    keyword: '电影推荐,电影剧情介绍,最新电影',
  },
  tv: {
    title: '电视剧',
    desc: '热播剧集、年代群像与悬疑短剧，一次看清完整剧情脉络与演职员阵容。',
    keyword: '电视剧资源,剧集推荐,电视剧剧情介绍',
  },
};

interface FilterGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  resetLabel: string;
}

function FilterGroup({ label, options, value, onChange, resetLabel }: FilterGroupProps) {
  const allOptions = [resetLabel, ...options];
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/80 py-3 sm:flex-row sm:items-start sm:gap-4">
      <p className="w-14 shrink-0 pt-1.5 text-xs font-medium text-foreground-500">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {allOptions.map((option) => {
          const active = value === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`rounded-md px-2.5 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                active
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'text-foreground-700 hover:bg-background-200/70'
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface PageProps {
  type: FilmType;
}

export default function CategoryPage({ type }: PageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({
    ...emptyFilters,
    genre: searchParams.get('genre') || emptyFilters.genre,
  });
  const [sort, setSort] = useState<SortKey>('updated');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);

  const genreOptions = genreOptionsByType[type];
  const copy = typeCopy[type];

  useEffect(() => {
    setFilters({
      ...emptyFilters,
      genre: searchParams.get('genre') || emptyFilters.genre,
    });
    setVisible(PAGE_SIZE);
  }, [type, searchParams]);

  useEffect(() => {
    applySeo({
      title: `${copy.title} - 同学的妈妈影视大全`,
      description: `同学的妈妈${copy.title}频道收录${copy.desc}`,
      keywords: `${copy.keyword},同学的妈妈`,
      path: `/category/${type}`,
    });
  }, [type, copy]);

  useEffect(() => {
    document.body.style.overflow = sheetOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sheetOpen]);

  const filtered = useMemo(() => {
    const list = films.filter((film) => {
      if (film.type !== type) return false;
      if (filters.region !== emptyFilters.region && film.region !== filters.region) return false;
      if (filters.year !== emptyFilters.year && String(film.year) !== filters.year) return false;
      if (filters.language !== emptyFilters.language && film.language !== filters.language) return false;
      if (
        filters.genre !== emptyFilters.genre &&
        !film.genres.includes(filters.genre) &&
        !film.tags.includes(filters.genre)
      ) {
        return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'popular') return b.popularity - a.popularity;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [filters, sort, type]);

  const shown = filtered.slice(0, visible);
  const activeCount = Object.entries(filters).filter(
    ([key, value]) => value !== emptyFilters[key as keyof Filters],
  ).length;

  const updateFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setVisible(PAGE_SIZE);
  };

  const resetFilters = () => {
    setFilters({ ...emptyFilters });
    setVisible(PAGE_SIZE);
    setSearchParams({});
  };

  const filterBar = (
    <div>
      <FilterGroup
        label="地区"
        options={regionOptions.slice(1)}
        value={filters.region}
        onChange={(value) => updateFilter('region', value)}
        resetLabel={emptyFilters.region}
      />
      <FilterGroup
        label="年份"
        options={yearOptions.slice(1)}
        value={filters.year}
        onChange={(value) => updateFilter('year', value)}
        resetLabel={emptyFilters.year}
      />
      <FilterGroup
        label="语言"
        options={languageOptions.slice(1)}
        value={filters.language}
        onChange={(value) => updateFilter('language', value)}
        resetLabel={emptyFilters.language}
      />
      <FilterGroup
        label="类型"
        options={genreOptions}
        value={filters.genre}
        onChange={(value) => updateFilter('genre', value)}
        resetLabel={emptyFilters.genre}
      />
    </div>
  );

  return (
    <div className="px-4 md:px-8 lg:px-12">
      <div className="pt-5 md:pt-6">
        <Breadcrumb items={[{ label: copy.title }]} />
      </div>

      <header className="mt-4 flex flex-col gap-4 border-b border-background-200 pb-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-label text-[11px] uppercase tracking-[0.2em] text-accent-600">{type === 'movie' ? 'Movies' : 'Series'}</p>
          <h1 className="mt-1.5 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            最新{copy.title}
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">{copy.desc}</p>
        </div>
        <div className="flex items-center gap-3 font-label text-xs text-foreground-500">
          <span className="rounded-md bg-secondary-100 px-2.5 py-1.5 text-secondary-900">
            共 {filtered.length} 部
          </span>
          <span className="hidden sm:inline">数据演示 · 每周整理</span>
        </div>
      </header>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
        <div className="hidden lg:col-span-3 lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 px-4 py-2">
            <div className="flex items-center justify-between border-b border-background-200/80 py-3">
              <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-filter-3-line text-foreground-500 w-4 h-4 flex items-center justify-center" />
                筛选条件
              </h2>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[11px] text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap"
                >
                  清空
                </button>
              )}
            </div>
            {filterBar}
            <div className="py-4">
              <p className="text-xs font-medium text-foreground-500">排序方式</p>
              <div className="mt-2 flex flex-col gap-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`flex items-center justify-between rounded-md px-2.5 py-2 text-xs transition-colors cursor-pointer ${
                      sort === option.key
                        ? 'bg-primary-50 font-medium text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {option.label}
                    {sort === option.key && (
                      <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-9">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="hide-scrollbar flex items-center gap-1.5 overflow-x-auto">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option.key
                      ? 'bg-foreground-950 font-medium text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 lg:hidden cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line w-3.5 h-3.5 flex items-center justify-center" />
              筛选
              {activeCount > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] font-bold text-foreground-950">
                  {activeCount}
                </span>
              )}
            </button>
          </div>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {shown.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
              <div className="mt-8 flex flex-col items-center gap-3">
                <p className="font-label text-[11px] text-foreground-500">
                  已显示 {shown.length} / {filtered.length} 部
                </p>
                {shown.length < filtered.length && (
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="inline-flex h-10 items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-6 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    加载更多
                    <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <EmptyState
              title="暂无符合条件的影视作品"
              description="当前筛选条件下没有匹配的内容，试试放宽地区、年份或类型限制。"
              onReset={resetFilters}
            />
          )}
        </div>
      </div>

      {sheetOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-foreground-950/50 anim-fade-in cursor-pointer"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-lg bg-background-50 p-5 anim-fade-up">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-base w-4 h-4 flex items-center justify-center" />
              </button>
            </div>
            {filterBar}
            <div className="border-b border-background-200/80 py-3">
              <p className="mb-2 text-xs font-medium text-foreground-500">排序方式</p>
              <div className="flex flex-wrap gap-1.5">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === option.key
                        ? 'bg-primary-500 font-medium text-background-50'
                        : 'bg-background-100 text-foreground-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 flex gap-2.5 pb-2">
              <button
                type="button"
                onClick={resetFilters}
                className="h-10 flex-1 rounded-md border border-background-300 text-sm font-medium text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="h-10 flex-[2] rounded-md bg-accent-500 text-sm font-semibold text-foreground-950 cursor-pointer whitespace-nowrap"
              >
                查看 {filtered.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}